import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { useMenu } from '@components/menu';
import { Show } from '@components/Show';
import Tooltip from '@components/Tooltip';
import BlockMenu from '@ContractBuilder/components/BlockMenu';
import { NavBlockIcon, sidebarBlockTextClasses } from '@ContractBuilder/components/shared-sidebar';
import { selectIsAnyMultiSelectActive, useMultiSelectStore } from '@ContractBuilder/store/multi-select.store';
import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';
import type { ResourceBlock } from '@domain/schemas/contract.schema';
import { BlockCheckboxController, CheckboxSpacer } from '@features/multi-select-blocks';
import { SwitchVerticalIcon as DragAndDropIcon } from '@heroicons/react/outline';
import { getBlockDisplayName, isClauseBlock } from '@root/helpers/blocks';
import { getClauseListEntryContent } from '@root/helpers/clauses';
import { scrollIntoView } from '@root/src/utils/scroll-into-view';
import clsx from 'clsx';

import { useBlockEditFormStore } from '../../../../block-edit';

const visibleOnHoverClasses = ['group-hover:opacity-100', 'opacity-0'];

interface SortedBlockItemProps {
  block: ResourceBlock;
  listeners?: DraggableSyntheticListeners;
  attributes?: DraggableAttributes;
  areOperationsHidden?: boolean;
  isDragDisabled?: boolean;
  isExcludedFromSummary?: boolean;
  shouldDisableOperations?: boolean;
}

const getContentForTooltip = (block: ResourceBlock) => {
  if (isClauseBlock(block) && block.clause_reference) {
    return getClauseListEntryContent(block);
  }

  return getBlockDisplayName(block);
};

export const SortedBlockItem: FC<SortedBlockItemProps> = ({
  block,
  listeners,
  attributes,
  areOperationsHidden = false,
  isDragDisabled = false,
  isExcludedFromSummary = false,
  shouldDisableOperations = false,
}) => {
  const { isOpen: isMenuOpen, onClose, onOpen } = useMenu();
  const editingBlockId = useBlockEditFormStore((state) => state.formValues?.id);
  const isAnyMultiSelectActive = useMultiSelectStore(selectIsAnyMultiSelectActive);

  const isEditing = editingBlockId === block.id;
  const scrollableId = `${block.id}-navigation`;

  useEffect(() => {
    if (editingBlockId === block.id) {
      scrollIntoView(scrollableId, { inline: 'nearest' });
    }
    // eslint-disable-next-line -- We only want to react to `editingBlockId` changing
  }, [editingBlockId]);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (shouldDisableOperations) {
      onCloseRef.current();
    }
  }, [shouldDisableOperations]);

  const canShowCheckbox = !shouldDisableOperations;

  return (
    <div className="flex w-full flex-nowrap items-center justify-between" id={scrollableId}>
      <div className="flex w-full flex-nowrap items-center gap-1.5 truncate">
        {canShowCheckbox ? (
          <BlockCheckboxController partId={block.section_id} blockId={block.id} blockName={block.name} />
        ) : (
          <CheckboxSpacer />
        )}
        <NavBlockIcon isActive={isEditing} isExcluded={isExcludedFromSummary} />

        <Tooltip content={getContentForTooltip(block)}>
          <p
            className={sidebarBlockTextClasses({
              bold: isMenuOpen || isEditing,
              dimmed: isExcludedFromSummary,
            })}
          >
            {getBlockDisplayName(block)}
          </p>
        </Tooltip>
      </div>
      <div
        className={clsx(
          'flex items-center',
          (shouldDisableOperations || isAnyMultiSelectActive) && 'pointer-events-none opacity-30',
        )}
      >
        <Show
          when={
            !areOperationsHidden &&
            ((!isDragDisabled && !!listeners) || isAnyMultiSelectActive || shouldDisableOperations)
          }
        >
          <div
            data-testid={`${block.name}-draggable-block-trigger`}
            className={clsx(
              visibleOnHoverClasses,
              'group-hover:text-info-800 pl-1',
              !isAnyMultiSelectActive && 'cursor-grab active:cursor-grabbing',
              isMenuOpen ? 'text-info-800 opacity-100' : 'text-info-400',
            )}
          >
            <div className="relative h-6 w-6" {...(isAnyMultiSelectActive ? {} : { ...listeners, ...attributes })}>
              <DragAndDropIcon className="h-6 w-6" />
            </div>
          </div>
        </Show>
        <Show when={!areOperationsHidden}>
          <BlockMenu
            block={block}
            className={clsx(
              visibleOnHoverClasses,
              'group-hover:text-info-800 cursor-pointer',
              isMenuOpen ? 'text-info-800 opacity-100' : 'text-info-400',
            )}
            isOpen={isMenuOpen}
            onClose={onClose}
            onOpen={onOpen}
            section_id={block.section_id}
          />
        </Show>
      </div>
    </div>
  );
};
